import * as THREE from 'three';
import { DEVICE } from '../device';
import { PROJECTS } from './computer/data';
import './panel-fonts.css';

// The calendar on the slat wall and the smoked-glass wall clock show the real date and time in Dhaka (Asia/Dhaka,
// GMT+6) whatever the visitor's own time zone: both are drawn on canvases in the browser, crisp at close range, the
// clock every second and the calendar when the date changes. Blender bakes stand-ins (paint-studio-textures.mjs).

const TZ = 'Asia/Dhaka';
const UI = '"Segoe UI", system-ui, -apple-system, sans-serif';
// the panels' design language (panels.css): Panel Grotesk / Panel Mono (panel-fonts.css), gold on ink
const GROT = '"Panel Grotesk", "Bricolage Grotesque", system-ui, sans-serif';
const MONO = '"Panel Mono", "JetBrains Mono", "Cascadia Mono", Consolas, monospace';
const INK = '#0c0f0e', PAPER = '#ece6d8', TEXT = 'rgba(236,230,216,.78)', DIM = 'rgba(236,230,216,.5)', LINE = 'rgba(236,230,216,.16)';
const GOLD = '#e9b45e', OK = '#6fd39b';
const BN = '"Noto Sans Bengali", "Nirmala UI", "Shonar Bangla", sans-serif';
const BN_MONTHS = ['জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন', 'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const parts = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'short' });

export type DhakaTime = { y: number; m: number; d: number; h: number; min: number; s: number; wd: string };

export function dhakaNow(at = new Date()): DhakaTime {
  const p = Object.fromEntries(parts.formatToParts(at).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour, min: +p.minute, s: +p.second, wd: p.weekday };
}

/** "3:55 AM · Tuesday 21 October" in Dhaka. */
export function dhakaLabel(at = new Date()) {
  const t = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true }).format(at).toUpperCase();
  const day = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(at);
  return `${t} · ${day}`;
}

function canvas(w: number, h: number, scale: number) {
  const c = document.createElement('canvas');
  c.width = w * scale; c.height = h * scale;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  g.scale(scale, scale);
  return { c, g };
}

function texture(c: HTMLCanvasElement, renderer: THREE.WebGLRenderer) {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.flipY = false;   // glTF UVs
  t.anisotropy = renderer.capabilities.getMaxAnisotropy();
  return t;
}

/** The wall calendar: this month in Dhaka, Bangla and English, Bangladesh's weekend (Friday, Saturday) in red, today
 *  ringed, and the "Book a call" note (the calendar opens the booking panel). Drawn at 1200 x 1680. */
class Calendar {
  readonly tex: THREE.CanvasTexture;
  private c: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  private key = '';
  constructor(renderer: THREE.WebGLRenderer) {
    ({ c: this.c, g: this.g } = canvas(600, 840, 2));
    this.tex = texture(this.c, renderer);
  }
  /** Draw again on the next update (the fonts have arrived). */
  reset() { this.key = ''; }
  update(t: DhakaTime) {
    const key = `${t.y}-${t.m}-${t.d}`;
    if (key === this.key) return;
    this.key = key;
    const g = this.g;
    g.fillStyle = '#efe7d6'; g.fillRect(0, 0, 600, 840);
    g.fillStyle = '#b8261b'; g.fillRect(0, 0, 600, 190);
    g.textAlign = 'center'; g.textBaseline = 'alphabetic';
    g.fillStyle = '#fff4e2'; g.font = `700 64px ${BN}`; g.fillText(BN_MONTHS[t.m - 1], 300, 105);
    g.font = `600 34px ${UI}`; g.fillText(`${MONTHS[t.m - 1]} ${t.y}`, 300, 160);
    ['S', 'M', 'T', 'W', 'T', 'F', 'S'].forEach((d, i) => { g.fillStyle = i >= 5 ? '#b8261b' : '#6b5b45'; g.font = `700 26px ${UI}`; g.fillText(d, 60 + i * 80, 240); });
    const first = new Date(Date.UTC(t.y, t.m - 1, 1)).getUTCDay(), days = new Date(Date.UTC(t.y, t.m, 0)).getUTCDate();
    const rows = Math.ceil((first + days) / 7), step = rows > 5 ? 68 : 80;
    g.font = `500 30px ${UI}`;
    for (let d = 1; d <= days; d++) {
      const cell = first + d - 1, col = cell % 7, row = Math.floor(cell / 7), x = 60 + col * 80, y = 300 + row * step;
      if (d === t.d) {
        g.beginPath(); g.arc(x, y - 10, 29, 0, Math.PI * 2); g.fillStyle = '#1f7a55'; g.fill();
        g.fillStyle = '#ffffff';
      } else g.fillStyle = col >= 5 ? '#b8261b' : '#3b3226';
      g.fillText(String(d), x, y);
    }
    g.save(); g.translate(440, 772); g.rotate(-0.05);
    g.fillStyle = '#f6cb76'; g.fillRect(-120, -44, 240, 88);
    g.fillStyle = '#3b3226'; g.font = `700 28px ${UI}`; g.fillText('Book a call', 0, -4); g.font = `400 20px ${UI}`; g.fillText('pick a time ↗', 0, 26);
    g.restore();
    g.fillStyle = '#8a7a62'; g.font = `500 18px ${UI}`; g.textAlign = 'left'; g.fillText('DHAKA · GMT+6', 48, 790);
    this.tex.needsUpdate = true;
  }
}

/** The smoked-glass wall clock: warm-white hours and minutes with a soft glow, the colon breathing with the seconds,
 *  a fine seconds line underneath, and DHAKA, AM/PM, the weekday and date beside. Drawn at 2048 x 640. */
class Clock {
  readonly tex: THREE.CanvasTexture;
  private c: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  private key = '';
  constructor(renderer: THREE.WebGLRenderer) {
    ({ c: this.c, g: this.g } = canvas(1024, 320, DEVICE.kind === 'phone' ? 1 : 2));   // re-uploaded every half second
    this.tex = texture(this.c, renderer);
  }
  reset() { this.key = ''; }
  update(t: DhakaTime, ms: number) {
    const half = ms < 500 ? 0 : 1;
    const key = `${t.h}:${t.min}:${t.s}:${half}`;
    if (key === this.key) return;
    this.key = key;
    const g = this.g;
    const bg = g.createLinearGradient(0, 0, 1024, 320);
    bg.addColorStop(0, '#0b0e12'); bg.addColorStop(1, '#040506');
    g.fillStyle = bg; g.fillRect(0, 0, 1024, 320);
    const sheen = g.createLinearGradient(0, 0, 1024, 320);   // the glass catching the room
    sheen.addColorStop(0.18, 'rgba(255,255,255,0)'); sheen.addColorStop(0.3, 'rgba(255,255,255,.045)'); sheen.addColorStop(0.42, 'rgba(255,255,255,0)');
    g.fillStyle = sheen; g.fillRect(0, 0, 1024, 320);
    const h12 = t.h % 12 || 12;
    g.textBaseline = 'middle'; g.textAlign = 'left';
    g.shadowColor = 'rgba(255,170,80,.55)'; g.shadowBlur = 22;
    g.fillStyle = '#ffe6bd'; g.font = `200 200px ${UI}`;
    const hh = String(h12).padStart(2, '0'), mm = String(t.min).padStart(2, '0');
    const wH = g.measureText(hh).width, wC = g.measureText(':').width;
    g.fillText(hh, 64, 150);
    g.globalAlpha = half ? 0.35 : 1; g.fillText(':', 64 + wH + 4, 140); g.globalAlpha = 1;
    g.fillText(mm, 64 + wH + wC + 8, 150);
    g.shadowBlur = 0;
    // seconds
    const x0 = 72, x1 = 600;
    g.fillStyle = 'rgba(255,255,255,.08)'; g.fillRect(x0, 266, x1 - x0, 3);
    g.fillStyle = 'rgba(246,203,118,.85)'; g.fillRect(x0, 266, (x1 - x0) * (t.s + half * 0.5) / 60, 3);
    // the side column
    g.fillStyle = 'rgba(255,255,255,.1)'; g.fillRect(650, 58, 2, 204);
    g.textBaseline = 'alphabetic';
    g.fillStyle = '#f6cb76'; g.font = `600 30px ${UI}`;
    (g as unknown as { letterSpacing: string }).letterSpacing = '9px';
    g.fillText('DHAKA', 690, 104);
    (g as unknown as { letterSpacing: string }).letterSpacing = '2px';
    g.fillStyle = 'rgba(255,230,190,.9)'; g.font = `500 34px ${UI}`; g.fillText(t.h < 12 ? 'AM' : 'PM', 690, 160);
    g.fillStyle = 'rgba(255,230,190,.62)'; g.font = `500 26px ${UI}`;
    g.fillText(`${t.wd.toUpperCase()} · ${t.d} ${MONTHS[t.m - 1].slice(0, 3).toUpperCase()}`, 690, 210);
    g.fillText('GMT+6', 690, 252);
    (g as unknown as { letterSpacing: string }).letterSpacing = '0px';
    this.tex.needsUpdate = true;
  }
}

/** The ultrawide monitor's idle screen (SalmanOS off): the selected work in the panels' language, from `PROJECTS`, so
 *  it never goes stale. A mono marker label, a condensed capital heading, a numbered list on hairlines, the status
 *  as a square swatch. Read from 2-3 m away: nothing under ~34 px. Drawn at 2400 x 1050 (the baked texture's size). */
class Catalogue {
  readonly tex: THREE.CanvasTexture;
  private c: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  constructor(renderer: THREE.WebGLRenderer) {
    ({ c: this.c, g: this.g } = canvas(2400, 1050, DEVICE.kind === 'phone' ? 0.6 : 1));
    this.tex = texture(this.c, renderer);
  }
  draw() {
    const g = this.g, W = 2400, X = 110, R = W - 110;
    const ls = (px: number) => { (g as unknown as { letterSpacing: string }).letterSpacing = `${px}px`; };
    // setting `font` resets the stretch, so the condensed capitals set it after
    const font = (f: string, condensed = false) => { g.font = f; if (condensed && 'fontStretch' in g) (g as unknown as { fontStretch: string }).fontStretch = 'condensed'; };
    const fit = (s: string, w: number) => {
      if (g.measureText(s).width <= w) return s;
      while (s.length > 1 && g.measureText(`${s}…`).width > w) s = s.slice(0, -1).trimEnd();
      return `${s}…`;
    };
    g.fillStyle = INK; g.fillRect(0, 0, W, 1050);
    g.textBaseline = 'alphabetic'; g.textAlign = 'left';
    // [02] WORK: the index in gold, the word on a solid gold block
    font(`600 36px ${MONO}`); ls(4);
    g.fillStyle = GOLD; g.fillText('02', X, 128);
    const wx = X + g.measureText('02').width + 30, ww = g.measureText('WORK').width;
    g.fillRect(wx - 16, 88, ww + 28, 56);
    g.fillStyle = INK; g.fillText('WORK', wx, 128);
    g.textAlign = 'right'; g.fillStyle = DIM; g.fillText('KAZISALMAN.DEV', R, 128); g.textAlign = 'left';
    // the heading
    font(`800 190px ${GROT}`, true); ls(-3);
    g.fillStyle = PAPER; g.fillText('SELECTED WORK', X - 6, 310);
    // the list
    const rows = PROJECTS.slice(0, 6), top = 360, step = 111;
    g.fillStyle = LINE; g.fillRect(X, top, R - X, 2);
    rows.forEach((p, i) => {
      const y = top + step * (i + 1), base = y - 38;
      g.fillStyle = LINE; g.fillRect(X, y, R - X, 2);
      font(`500 36px ${MONO}`); ls(2);
      g.fillStyle = GOLD; g.fillText(String(i + 1).padStart(2, '0'), X, base);
      font(`760 76px ${GROT}`, true); ls(-1);
      g.fillStyle = PAPER; g.fillText(fit(p.name.toUpperCase(), 660), X + 110, base + 6);
      font(`400 38px ${GROT}`); ls(0);
      g.fillStyle = TEXT; g.fillText(fit(p.kicker, 500), X + 810, base);
      // status: a square swatch, then the word, right-aligned; the stack fills what is left between
      const word = p.status.toUpperCase();
      font(`500 34px ${MONO}`); ls(1);
      const sw = R - g.measureText(word).width - 44;
      g.textAlign = 'right'; g.fillStyle = PAPER; g.fillText(word, R, base); g.textAlign = 'left';
      g.fillStyle = p.status === 'Live' ? OK : p.status === 'Coming soon' || p.status === 'In development' ? GOLD : DIM;
      g.fillRect(sw, base - 26, 24, 24);
      const sx = X + 1350;
      font(`400 32px ${MONO}`); ls(0);
      g.fillStyle = DIM; g.fillText(fit(p.stack.slice(0, 3).join(' · '), sw - 50 - sx), sx, base);
    });
    ls(0);
    this.tex.needsUpdate = true;
  }
}

export class LivePrints {
  private calendar: Calendar | null = null;
  private clock: Clock | null = null;
  private catalogue: Catalogue | null = null;
  constructor(private renderer: THREE.WebGLRenderer) {}

  /** Swap the baked stand-ins on these screen meshes for the live canvases. */
  attach(calendarMesh: THREE.Mesh | undefined, clockMesh: THREE.Mesh | undefined, catalogueMesh?: THREE.Mesh) {
    const use = (mesh: THREE.Mesh | undefined, tex: THREE.Texture) => {
      if (!mesh) return;
      const mat = mesh.material as THREE.MeshBasicMaterial;
      mat.map = tex; mat.needsUpdate = true;
    };
    if (calendarMesh) { this.calendar = new Calendar(this.renderer); use(calendarMesh, this.calendar.tex); }
    if (clockMesh) { this.clock = new Clock(this.renderer); use(clockMesh, this.clock.tex); }
    if (catalogueMesh) { this.catalogue = new Catalogue(this.renderer); this.catalogue.draw(); use(catalogueMesh, this.catalogue.tex); }
    // the fonts arrive after the first frame on a cold visit: draw again once they have (a canvas does not ask for the
    // panel fonts by itself, so load them here)
    const panelFonts = this.catalogue ? [`800 150px ${GROT}`, `400 38px ${GROT}`, `500 36px ${MONO}`].map((f) => document.fonts?.load(f)) : [];
    void Promise.all([document.fonts?.ready, ...panelFonts]).catch(() => undefined).then(() => {
      this.calendar?.reset(); this.clock?.reset(); this.catalogue?.draw(); this.update();
    });
    this.update();
  }

  update() {
    const now = new Date(), t = dhakaNow(now);
    this.calendar?.update(t);
    this.clock?.update(t, now.getMilliseconds());
  }
}
